import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { useWebSocket } from './WebSocketContext'
import { audioService, decompressFlate } from '../services/audio'
import { apiFetch } from '../services/apiClient'

export interface Animation { Name: string }

export interface ChatMessage {
  id: string
  type: 'user' | 'bot' | 'system' | 'error'
  text: string
  animations?: Animation[]
}

export interface Metrics {
  llm_first_sentence_ms?: number
  tts_fast_ms?: number
  tts_quality_ms?: number
  a2f_processing_ms?: number
  first_frame_ms?: number
  storyteller_ms?: number
  total_ms?: number
}

export interface StoryDebugData {
  enabled: boolean
  phase_id?: string
  turn_count?: number
  max_turns?: number
  tone?: string
  prefabs?: Array<{ id: string; condition: string; goto: string }>
  objectives?: Array<{
    id: string
    description: string
    completed?: boolean
    effectiveness?: string
    urgency?: string
    steering?: string
  }>
  player_state?: Record<string, string>
  reasoning?: string
}

interface ChatContextType {
  messages: ChatMessage[]
  metrics: Metrics
  suggestions: string[]
  mode: string
  setMode: (m: string) => void
  sendMessage: (text: string) => void
  storyDebug: StoryDebugData | null
  storytellerPending: boolean
  resetSession: () => void
  buildVersion: string
}

const ChatContext = createContext<ChatContextType>(null!)

function uid() {
  return Math.random().toString(36).slice(2)
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { connected, send, onMessage } = useWebSocket()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [metrics, setMetrics] = useState<Metrics>({})
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [mode, setModeState] = useState(() => localStorage.getItem('chatMode') || 'TextIn')
  const [storyDebug, setStoryDebug] = useState<StoryDebugData | null>(null)
  const [storytellerPending, setStorytellerPending] = useState(false)
  const [buildVersion, setBuildVersion] = useState('')
  const metricsReceivedRef = useRef(false)
  const ttsQualitySetRef = useRef(false)

  function addMessage(text: string, type: ChatMessage['type'], animations?: Animation[]) {
    setMessages(prev => [...prev, { id: uid(), type, text, animations }])
  }

  function setMode(m: string) {
    setModeState(m)
    localStorage.setItem('chatMode', m)
  }

  function sendMessage(text: string) {
    if (!text.trim() || !connected) return
    const msg = { type: mode, message: text }
    setMetrics({ llm_first_sentence_ms: undefined }) // reset display
    setSuggestions([])
    send(msg)
    addMessage(text, 'user')
  }

  function resetSession() {
    localStorage.removeItem('sessionId')
    setMessages([])
    audioService.resetState()
    
    const url = new URL(window.location.pathname + window.location.search, window.location.origin)
    url.searchParams.delete('session_id')

    window.history.replaceState(null, '', url.toString()) // clear URL params
    window.location.reload()
  }

  useEffect(() => {
    fetch('/version').then(r => r.json()).then((d: { version: string }) => setBuildVersion(d.version)).catch(() => {})
  }, [])

  useEffect(() => {
    const unsubs: Array<() => void> = []

    unsubs.push(onMessage('session', (msg) => {
      const id = msg.message
      if (id) {
        // The history route needs the caller's credential (cookie, or the
        // local-dev X-API-Key), so it goes through the shared client rather
        // than a bare fetch. A refusal (not our session) reads as no history.
        apiFetch<Array<{ role: string; content: string }>>(`/sessions/${id}/history`)
          .catch(() => [] as Array<{ role: string; content: string }>)
          .then(data => {
            const historyMsgs: ChatMessage[] = data
              .filter(m => m.role === 'user' || m.role === 'assistant')
              .map(m => ({ id: uid(), type: m.role === 'user' ? 'user' as const : 'bot' as const, text: m.content }))
            setMessages(prev => [...historyMsgs, ...prev, { id: uid(), type: 'system' as const, text: 'Connected to session' }])
          })
          .catch(() => { addMessage('Connected to session', 'system') })
      } else {
        addMessage('Connected to session', 'system')
      }
    }))

    unsubs.push(onMessage('TextOut', (msg) => {
      const data = msg.data as { text?: string; animations?: Animation[]; id?: number } | string
      const text = typeof data === 'object' && data.text !== undefined ? data.text : (typeof data === 'string' ? data : '')
      const anims = typeof data === 'object' ? (data as { animations?: Animation[] }).animations : []
      addMessage(text, 'bot', anims)
      if (typeof data === 'object' && (data as { id?: number }).id !== undefined) {
        audioService.addPendingId((data as { id: number }).id)
      }
    }))

    unsubs.push(onMessage('NewResponse', (msg) => {
      audioService.resetState()
      metricsReceivedRef.current = false
      ttsQualitySetRef.current = false
      const data = msg.data as { prefab?: string } | undefined
      if (data?.prefab) {
        addMessage(`[Prefab: ${data.prefab}]`, 'system')
      }
    }))

    unsubs.push(onMessage('AudioAnimation', (msg) => {
      const data = msg.data as { id: number; audio?: string } | undefined
      if (data?.id !== undefined) {
        const audio = data.audio ? decompressFlate(data.audio) : null
        audioService.onAudioAnimation(data.id, audio)
      }
    }))

    unsubs.push(onMessage('AudioOut', (msg) => {
      const data = msg.data as { id: number; audio?: string } | undefined
      if (data?.id !== undefined) {
        const audio = data.audio ? decompressFlate(data.audio) : null
        audioService.onAudioAnimation(data.id, audio)
      }
    }))

    unsubs.push(onMessage('StreamChunk', (msg) => {
      const data = msg.data as { id: number; audio?: string; done?: boolean } | undefined
      if (data?.id !== undefined) {
        audioService.onStreamChunk(data.id, data.audio || null, !!data.done)
      }
    }))

    unsubs.push(onMessage('Metrics', (msg) => {
      const data = msg.data as Metrics | undefined
      if (!data) return
      if (!metricsReceivedRef.current) {
        metricsReceivedRef.current = true
        setMetrics(data)
        if (data.tts_quality_ms) ttsQualitySetRef.current = true
      } else if (!ttsQualitySetRef.current && data.tts_quality_ms) {
        ttsQualitySetRef.current = true
        setMetrics(prev => ({ ...prev, tts_quality_ms: data.tts_quality_ms }))
      }
    }))

    unsubs.push(onMessage('StorytellerMetrics', (msg) => {
      const data = msg.data as { storyteller_ms?: number } | undefined
      if (data) setMetrics(prev => ({ ...prev, storyteller_ms: data.storyteller_ms }))
    }))

    unsubs.push(onMessage('SuggestedResponses', (msg) => {
      const data = msg.data as { suggestions?: string[] } | undefined
      if (data?.suggestions?.length) setSuggestions(data.suggestions)
    }))

    unsubs.push(onMessage('StorytellerPending', () => {
      setStorytellerPending(true)
    }))

    unsubs.push(onMessage('StoryDebug', (msg) => {
      setStorytellerPending(false)
      const data = msg.data as StoryDebugData
      if (data?.enabled) setStoryDebug(data)
    }))

    unsubs.push(onMessage('Error', (msg) => {
      addMessage(`Error: ${msg.message} - ${msg.data}`, 'error')
    }))

    return () => unsubs.forEach(u => u())
  }, [onMessage])

  return (
    <ChatContext.Provider value={{
      messages, metrics, suggestions, mode, setMode, sendMessage,
      storyDebug, storytellerPending, resetSession, buildVersion
    }}>
      {children}
    </ChatContext.Provider>
  )
}

export const useChat = () => useContext(ChatContext)
